import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { LoadingScreen } from 'src/components/loading-screen';
import { Page } from 'src/components/Page';
import { useStores } from 'src/models';
import { PATH_AUTH, PATH_PAGE } from 'src/routes/paths';
import { isMockAuthenticated } from 'src/services/oauth/mock-auth';

type AuthGuardProps = {
  children: React.ReactNode;
};

/**
 * AuthGuard
 *
 * 우선순위:
 *   1. 실 BE 인증 사용자 (authStore.isAuthenticated)
 *   2. Mock 인증 사용자 (sessionStorage 기반 — BE 미연결 상태 우회)
 *   3. 둘 다 없으면 → 로그인 화면으로
 *
 * 문진(medical / drug) 가드는 실 BE 연동 후에만 동작 (mock 사용자는 패스).
 */
function AuthGuard({ children }: AuthGuardProps) {
  const { authStore } = useStores();
  const { isAuthenticated, isInitialized } = authStore;
  const mockAuth = isMockAuthenticated();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [_checked, setChecked] = useState(false);

  // 문진 완료가 필요한 메인 경로
  const requireSurveyPaths = useMemo(() => [PATH_PAGE.home, PATH_PAGE.calendar.root], []);

  // 문진/마이 등 가드 예외 경로
  const excludedPaths = useMemo(
    () => [
      PATH_PAGE.medicalQuestion.root,
      PATH_PAGE.drugQuestion.root,
      PATH_PAGE.medicineSettings.root,
      PATH_PAGE.myPage.root,
    ],
    [],
  );

  useEffect(() => {
    const check = async () => {
      const userProfile = authStore.user;
      // 실 BE 사용자가 없거나, Mock 인증 상태면 문진 가드 스킵
      if (!userProfile) return;

      if (
        excludedPaths.some((p) => pathname.startsWith(p)) ||
        !requireSurveyPaths.some((p) => pathname.startsWith(p))
      ) {
        return;
      }

      const hasIncompleteMedical = userProfile.medicalQuestionCompleteYn?.some(
        (item) => item.medicalQuestionCompleteYn === 'N',
      );
      const hasIncompleteDrug = userProfile.drugQuestionCompleteYn?.some(
        (item) => item.drugQuestionCompleteYn === 'N',
      );

      let redirectPath: string | null = null;
      if (hasIncompleteMedical) {
        redirectPath = PATH_PAGE.medicalQuestion.root;
      } else if (hasIncompleteDrug) {
        redirectPath = PATH_PAGE.drugQuestion.root;
      }

      if (redirectPath && pathname !== redirectPath) {
        navigate(redirectPath);
      }
      setChecked(true);
    };

    check();
  }, [
    authStore.user,
    navigate,
    pathname,
    isInitialized,
    isAuthenticated,
    excludedPaths,
    requireSurveyPaths,
  ]);

  // 실 BE 인증 초기화 대기
  if (!isInitialized && !mockAuth) {
    return <LoadingScreen />;
  }

  // 어느 쪽도 인증 안 됨 → 로그인으로
  if (!isAuthenticated && !mockAuth) {
    return <Navigate to={PATH_AUTH.login} replace state={{ from: pathname }} />;
  }

  return (
    <Page id="auth" style={{ padding: 0, height: '100%' }}>
      {children}
    </Page>
  );
}

export default observer(AuthGuard);
